<template>
  <!-- ── Account controls ────────────────────────────────────────────────
       Organisation · user · language · theme, pinned under the last
       destination of the sidebar. In the desktop rail they shed their labels
       the same way the nav rows do. The bar's right side stays free for the
       module (see TopBar's `#pinksy-topbar-actions`). -->
  <div class="sh-foot" :class="{ 'sh-foot--rail': rail }">
    <!-- 🔴 No pinned organisation row here (D118). A WHITE_LABEL app was pinned to the ONE company
         that bought it (D27) and named it above the account controls, never switchable. There is no
         such pinning: which company a session is ACTING FOR is a live choice, and
         OrganisationSwitcher.vue is where it is shown and changed. -->
    <div class="sh-foot__account">
      <!-- ── Who am I ────────────────────────────────────────────────────
           Name beside the avatar, not just an initial: a bare monogram is
           indistinguishable from the "?" fallback. -->
      <v-menu location="top start" @update:model-value="onUserMenu">
        <template #activator="{ props }">
          <button type="button" class="sh-foot__row sh-foot__user" :title="rail ? userFullName : undefined" v-bind="props">
            <span class="sh-foot__avatar">
              <img v-if="userAvatar" :src="userAvatar" :alt="userFullName" referrerpolicy="no-referrer" @error="avatarBroke" />
              <span v-else>{{ userInitials }}</span>
              <!-- Signed in, on this device. Not presence for anybody else to see — nothing here is
                   published — it says the session is LIVE, which is the one thing this row can
                   honestly assert about a person looking at their own screen. -->
              <span class="sh-foot__dot" aria-hidden="true"></span>
            </span>
            <span v-if="!rail && userFullName" class="sh-foot__label sh-foot__user-name">{{ userFullName }}</span>
            <v-icon v-if="!rail" size="14" class="sh-foot__chev">unfold_more</v-icon>
          </button>
        </template>
        <div class="standalone-menu">
          <div class="standalone-menu__user">
            <span class="sh-foot__avatar sh-foot__avatar--lg">
              <img v-if="userAvatar" :src="userAvatar" :alt="userFullName" referrerpolicy="no-referrer" @error="avatarBroke" />
              <span v-else>{{ userInitials }}</span>
            </span>
            <span class="standalone-menu__user-id">
              <span class="standalone-menu__greeting">{{ greeting }}</span>
              <span class="standalone-menu__user-name">{{ userFullName || t("footer.signedIn") }}</span>
              <!-- The handle where there is one: a person recognises their own e-mail, and a passport id is
                   for support, not for them. It falls back to the id so the line is never empty. -->
              <span class="standalone-menu__user-email">{{ session.user?.handle || session.user?.userHash }}</span>
            </span>
          </div>
          <div class="standalone-menu__divider"></div>

          <!-- 🔴 **MY PROFILE, WHERE THE PERSON IS NAMED** (D52, D67).

               Devin: *"there should be a My Profile… it's my profile as a person. It should not be
               under the people section. that is where it's shared to."* So the way in is the menu
               their own face opens, above the settings, because it is a DESTINATION and the two
               below it are settings — and it is not in the sidebar's Directory, which is the list of
               people you are looking for. -->
          <button type="button" class="sh-row sh-foot__profile" data-testid="my-profile" @click="openProfile">
            <v-icon size="17" class="standalone-menu__row-icon">badge</v-icon>
            <span class="sh-row__label">{{ t("footer.myProfile") }}</span>
          </button>
          <!-- 🔴 How a person gets a passkey (PNK-137). Beside the profile because it is
               also a DESTINATION about themselves, and above the settings for the same
               reason the profile is. -->
          <button type="button" class="sh-row sh-foot__security" data-testid="my-security" @click="openSecurity">
            <v-icon size="17" class="standalone-menu__row-icon">fingerprint</v-icon>
            <span class="sh-row__label">{{ t("security.title") }}</span>
          </button>
          <div class="standalone-menu__divider"></div>

          <!-- ── 🔴 THE SETTINGS A PERSON CHANGES ABOUT THEMSELVES (D52) ──────
               Language and appearance belong to the human, not to the app, so they
               live where the human is named — beside their own face — rather than as
               two unlabelled glyphs loose in the footer.

               Both are drawn as the CHOICES, with the current one pressed, not as a
               toggle. A moon icon cannot say whether it means "you are dark" or "go
               dark", and everybody has guessed wrong at least once; two named
               controls have no such question, and reading the state is what a
               settings menu is for. -->
          <div class="sh-foot__setting" role="group" :aria-label="t('footer.language')">
            <v-icon size="17" class="standalone-menu__row-icon">translate</v-icon>
            <span class="sh-row__label">{{ t("footer.language") }}</span>
            <LocaleSwitch class="sh-foot__setting-control" />
          </div>

          <div class="sh-foot__setting" role="group" :aria-label="t('footer.appearance')">
            <v-icon size="17" class="standalone-menu__row-icon">contrast</v-icon>
            <span class="sh-row__label">{{ t("footer.appearance") }}</span>
            <div class="sh-choice sh-foot__setting-control">
              <button
                type="button"
                class="sh-choice__btn"
                :class="{ 'sh-choice__btn--active': !darkMode }"
                :aria-pressed="!darkMode ? 'true' : 'false'"
                data-testid="theme-light"
                @click="setDarkMode(false)"
              >
                {{ t("theme.light") }}
              </button>
              <button
                type="button"
                class="sh-choice__btn"
                :class="{ 'sh-choice__btn--active': darkMode }"
                :aria-pressed="darkMode ? 'true' : 'false'"
                data-testid="theme-dark"
                @click="setDarkMode(true)"
              >
                {{ t("theme.dark") }}
              </button>
            </div>
          </div>

          <div class="standalone-menu__divider"></div>
          <!-- Only while Chromium is holding an install prompt for this origin
               (src/pwa/install.ts) — an already-installed app, and every
               browser that does not fire the event, simply never shows it. -->
          <button v-if="canInstall" type="button" class="sh-row sh-foot__install" @click="install">
            <v-icon size="17" class="standalone-menu__row-icon">add_to_home_screen</v-icon>
            <span class="sh-row__label">{{ t("footer.installApp") }}</span>
          </button>
          <button type="button" class="sh-row sh-foot__signout" @click="session.signOut()">
            <v-icon size="17" class="standalone-menu__row-icon">logout</v-icon>
            <span class="sh-row__label">{{ t("common.signOut") }}</span>
          </button>
        </div>
      </v-menu>

    </div>
    <!-- On a phone the bottom rail's terms · release · new version · help live here (BottomRail.vue). -->
    <BottomRail v-if="mobile" in-drawer />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useDisplay } from "vuetify";
import BottomRail from "./BottomRail.vue";
import { useI18n } from "vue-i18n";
import { useThemeToggle } from "./useThemeToggle";
import { useSessionStore } from "@/stores/session";
import LocaleSwitch from "@/i18n/LocaleSwitch.vue";
import { navigateToUrl } from "single-spa";
import { canInstall, promptInstall } from "@/pwa/install";
import { SECURITY_ROUTE } from "@/nav/shellRoutes";

/**
 * Where a person's own profile lives (D67). Named by section (D164a): a bare `/profile` is the person's landing,
 * which for somebody with no staff company is her work, not her record.
 */
export const PROFILE_ROUTE = "/profile?section=profile";



export type GreetingKey =
  | "footer.greetingNight"
  | "footer.greetingMorning"
  | "footer.greetingAfternoon"
  | "footer.greetingEvening"
  | "footer.greetingLate";

/** Time-of-day greeting for the user menu — the message key, translated by the caller. */
export function greetingKeyFor(hour: number): GreetingKey {
  if (hour < 5) return "footer.greetingNight";
  if (hour < 12) return "footer.greetingMorning";
  if (hour < 17) return "footer.greetingAfternoon";
  if (hour < 22) return "footer.greetingEvening";
  return "footer.greetingLate";
}

/** "Jane Doe" → "JD"; a Passport id (no display name) → its first character. */
export function initialsOf(displayName: string | null, userHash: string | null): string {
  const name = (displayName ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/);
    const first = parts[0]?.charAt(0) ?? "";
    const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
    return `${first}${last}`.toUpperCase();
  }
  return (userHash ?? "?").charAt(0).toUpperCase() || "?";
}

export default defineComponent({
  name: "SidebarFooter",
  components: { LocaleSwitch, BottomRail },
  props: {
    /** Desktop icon rail — labels and chevrons drop, tooltips carry the names. */
    rail: { type: Boolean, default: false },
  },
  setup() {
    const session = useSessionStore();
    const { t } = useI18n();
    const { mobile } = useDisplay();

    // Shared with the pre-auth pages — see useThemeToggle.ts.
    const { darkMode, setDarkMode } = useThemeToggle();

    /**
     * The menu opens with a word for the time of day, then the name — a
     * greeting is the smallest thing that makes the chrome feel like it knows
     * who is there.
     */
    const greetingKey = ref<GreetingKey>(greetingKeyFor(new Date().getHours()));
    const greeting = computed(() => t(greetingKey.value));
    /** Re-read the clock each time the menu opens — a tab left open all day must not say "Good morning" at 15:00. */
    function onUserMenu(open: boolean): void {
      if (open) greetingKey.value = greetingKeyFor(new Date().getHours());
    }

    const userFullName = computed(() => session.user?.displayName?.trim() || "");
    /** The URL that would not load — so a NEW photograph is tried afresh, and a dead one yields to the initials. */
    const brokenAvatar = ref<string | null>(null);
    const userAvatar = computed(() => {
      const avatar = session.user?.avatar || null;
      return avatar === brokenAvatar.value ? null : avatar;
    });
    function avatarBroke(): void {
      brokenAvatar.value = session.user?.avatar ?? null;
    }
    const userInitials = computed(() => initialsOf(session.user?.displayName ?? null, session.user?.userHash ?? null));

    return {
      t,
      mobile,
      session,
      darkMode,
      setDarkMode,
      userFullName,
      userAvatar,
      avatarBroke,
      userInitials,
      greeting,
      onUserMenu,
      canInstall,
      install: promptInstall,
      /** The person's own record (D67). Closing the menu is the caller's business — navigating away unmounts it. */
      openProfile: () => navigateToUrl(PROFILE_ROUTE),
      openSecurity: () => navigateToUrl(SECURITY_ROUTE),
    };
  },
});
</script>

<style scoped>
.sh-foot {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--sh-hairline);
}
.sh-foot__account {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
}
.sh-foot--rail .sh-foot__account {
  flex-direction: column;
}

/* Same pill as a nav row — the footer is part of the sidebar, not a second
   toolbar bolted under it. */
.sh-foot__row {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 6px;
  border: none;
  border-radius: var(--sh-radius);
  background: transparent;
  color: var(--sh-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background var(--sh-motion) ease,
    transform var(--sh-motion) var(--sh-ease);
}
.sh-foot__row:hover {
  background: var(--sh-hover);
}
.sh-foot__row:active {
  transform: scale(0.985);
}
.sh-foot--rail .sh-foot__row {
  justify-content: center;
  padding: 0;
  height: 40px;
}
.sh-foot__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sh-foot__chev {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
}
.sh-foot__user {
  flex: 1 1 auto;
}

/* ── A setting inside the menu: label left, its choices right (D52) ──────
   The control is pushed to the trailing edge so both settings' values line
   up in one column — the eye reads "what is it set to" down that column
   rather than hunting along each row. */
.sh-foot__setting {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 5px 12px;
  color: var(--sh-text);
  font-size: 13px;
}
.sh-foot__setting .sh-row__label {
  flex: 1 1 auto;
  text-align: left;
}
.sh-foot__setting-control {
  flex: 0 0 auto;
}

/* 🔴 Signed in, on this device — drawn on the avatar's edge so it reads as a
   property of the person rather than a badge floating beside them. Overflow is
   visible on the wrapper only; the image itself still clips to the circle. */
.sh-foot__dot {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 2px rgb(var(--v-theme-surface));
}

/* People are circles (brand marks are the white squares). */
.sh-foot__avatar {
  position: relative;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  background: rgb(var(--v-theme-secondary));
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
/* The avatar gains a brand ring under the pointer, and the chevron beside
   it nudges up — "this opens something". */
.sh-foot__avatar {
  transition:
    box-shadow var(--sh-motion) ease,
    transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-foot__user:hover .sh-foot__avatar {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.45);
  transform: scale(1.06);
}
.sh-foot__chev {
  transition: transform var(--sh-motion) var(--sh-ease);
}
.sh-foot__row:hover .sh-foot__chev {
  transform: translateY(-1px);
}
.sh-foot__avatar--lg {
  width: 38px;
  height: 38px;
  font-size: 14px;
}
.sh-foot__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* The sun/moon tilts under the pointer, then turns over on the click
   (`sh-icon-swap`, standalone.css). */

/* ── Touch targets ─────────────────────────────────────────────────────
   Same breakpoint and reasoning as Sidebar.vue's rows. */
@media (max-width: 767.98px) {
  .sh-foot__row {
    height: 44px;
  }
  .sh-foot__label {
    font-size: 14px;
  }
}
</style>

<!-- Menus teleport to <body>, outside this component's scope — these two
     surfaces are styled globally instead. -->
<style>
.standalone-menu {
  min-width: 230px;
  padding: 6px;
  border-radius: 12px;
  background: rgb(var(--v-theme-card));
  border: 1px solid var(--sh-hairline);
  box-shadow: 0 18px 40px -20px rgba(0, 0, 0, 0.6);
}
.standalone-menu__label {
  padding: 5px 8px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
}
.standalone-menu__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
}
.standalone-menu__greeting {
  display: block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 1px;
}
.standalone-menu__user-id {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}
.standalone-menu__user-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-menu__user-email {
  font-size: 11px;
  color: var(--sh-text-45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-menu__divider {
  height: 1px;
  margin: 5px 0;
  background: var(--sh-hairline);
}
.standalone-menu__row-icon {
  color: var(--sh-text-60) !important;
}
.standalone-menu .sh-row {
  height: 36px;
}
.standalone-menu .sh-row--selected {
  background: var(--sh-active);
}
</style>
